import { useEffect } from "react"
import { datadogLogs } from "@datadog/browser-logs"
import { datadogRum, type Site } from "@datadog/browser-rum"
import { reactPlugin } from "@datadog/browser-rum-react"
import { useIsClient } from "@octopus-energy/utils"
import { env } from "@oefr/env/client"
import { useSession } from "../blueprint-auth/client"
import { LOGS_NOISE_FILTERS } from "./filters/logsNoiseFilters"
import { RUM_NOISE_FILTERS } from "./filters/rumNoiseFilters"
import { obfuscateGraphqlVariables } from "./utils/obfuscateGraphqlVariables"

const SITE: Site = "datadoghq.eu"

// Only initialize when not in local development — keeps sendBeacon calls
// out of the Playwright test browser, which can't intercept them via page.route().
if (env.NEXT_PUBLIC_VERCEL_ENV !== "development") {
    const DATADOG_COMMON_CONFIG = {
        clientToken: env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN,
        env: env.NEXT_PUBLIC_VERCEL_ENV,
        site: SITE,
        service: "oefr-consumer-site",
        version: env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA,
    }

    datadogRum.init({
        ...DATADOG_COMMON_CONFIG,
        applicationId: env.NEXT_PUBLIC_DATADOG_APPLICATION_ID,
        sessionSampleRate: 100,
        sessionReplaySampleRate: 1,
        trackUserInteractions: true,
        trackResources: true,
        trackLongTasks: true,
        defaultPrivacyLevel: "mask",
        plugins: [reactPlugin({ router: false })],
        beforeSend: (event) => {
            if (event.type === "error") {
                const normalized = { message: event.error.message, source: event.error.source }

                if (RUM_NOISE_FILTERS.some((filter) => filter(normalized))) {
                    return false
                }

                if (event.error.stack) {
                    event.error.stack = obfuscateGraphqlVariables(event.error.stack)
                }
            }

            return true
        },
    })

    datadogLogs.init({
        ...DATADOG_COMMON_CONFIG,
        forwardErrorsToLogs: true,
        forwardConsoleLogs: ["error"],
        sessionSampleRate: 100,
        beforeSend: (event) => {
            if (event.status === "error") {
                const normalized = { message: event.message, source: event.origin }

                if (LOGS_NOISE_FILTERS.some((filter) => filter(normalized))) {
                    return false
                }

                if (event.error?.stack) {
                    event.error.stack = obfuscateGraphqlVariables(event.error.stack)
                }
            }

            return true
        },
    })
}

export default function DatadogInit() {
    const { data: session } = useSession()
    const isClient = useIsClient()

    useEffect(() => {
        if (!isClient || typeof datadogRum?.setUser !== "function") {
            return
        }

        if (session?.isAuthenticated && session?.sub) {
            datadogRum.setUser({
                id: session.sub,
            })
        }
    }, [isClient, session?.isAuthenticated, session?.sub])

    return null
}
